// security/PermissionContext.tsx
import type { ReactNode } from 'react';
import { createContext, useCallback, useContext, useMemo } from 'react';
import {
  buildConnectorPermissionMap,
  buildPermissionMap,
  getConnectorEffectiveRole,
  getEffectiveRole,
  roleRank,
} from '../pages/security/permissions';
import type {
  TeamConnectionRoleSchemaModel,
  TeamRole,
  TeamRoleSchemaModel,
} from '../pages/security/type';

type PermissionContextValue = {
  /** Effective role for this path (including inheritance), or null */
  getRoleForPath: (path: string) => TeamRole | null;
  getConnectorRoleForPath: (id: string, path: string) => TeamRole | null;
  /** True if effectiveRole(path) >= minRole */
  canAtLeast: (path: string, minRole: TeamRole) => boolean;
  connectorCanAtLeast: (id: string, path: string, minRole: TeamRole) => boolean;
  /** True once the permission schema has been loaded from the API */
  permissionsLoaded: boolean;
  refreshPremissionContext: any;
};

const PermissionContext = createContext<PermissionContextValue | undefined>(undefined);

type PermissionProviderProps = {
  schema: TeamRoleSchemaModel[] | null; // FLATTENED list from backend
  connectionschema: TeamConnectionRoleSchemaModel | null; // FLATTENED list from backend
  refreshPremissionContext: any;
  children: ReactNode;
};

export const PermissionProvider = ({
  schema,
  connectionschema,
  refreshPremissionContext,
  children,
}: PermissionProviderProps) => {
  //console.log("[PermissionProvider] start");

  // 1) Build base map: path -> highest direct role
  const map = useMemo(() => buildPermissionMap(schema), [schema]);
  const connectionMap = useMemo(
    () => buildConnectorPermissionMap(connectionschema),
    [connectionschema],
  );

  // 2) Use inheritance when resolving specific path
  const getRoleForPath = useCallback(
    (path: string): TeamRole | null => getEffectiveRole(map, path),
    [map],
  );

  const getConnectorRoleForPath = useCallback(
    (id: string, path: string): TeamRole | null =>
      getConnectorEffectiveRole(connectionMap, map, id, path),
    [connectionMap],
  );

  const canAtLeast = useCallback(
    (path: string, minRole: TeamRole): boolean => {
      const role = getRoleForPath(path);
      if (!role) return false;
      return roleRank[role] >= roleRank[minRole];
    },
    [getRoleForPath],
  );

  const connectorCanAtLeast = useCallback(
    (id: string, path: string, minRole: TeamRole): boolean => {
      const role = getConnectorRoleForPath(id, path);
      if (!role) return false;
      return roleRank[role] >= roleRank[minRole];
    },
    [getConnectorRoleForPath],
  );

  const permissionsLoaded = schema !== null;

  return (
    <PermissionContext.Provider
      value={{
        getRoleForPath,
        getConnectorRoleForPath,
        canAtLeast,
        connectorCanAtLeast,
        permissionsLoaded,
        refreshPremissionContext,
      }}
    >
      {children}
    </PermissionContext.Provider>
  );
};

export const usePermissions = () => {
  const ctx = useContext(PermissionContext);
  if (!ctx) {
    throw new Error('usePermissions must be used inside PermissionProvider');
  }
  return ctx;
};
